 
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { ComponentOption, ComponentCategory } from '@smartphone-tycoon/shared-core';
import { components as baseComponents } from '@smartphone-tycoon/shared-core';

interface ComponentRelease {
  componentId: string;
  manufacturer: string;
  releaseYear: number;
  releaseMonth: number;
  techScore: number;
  cost: number;
  price: number;
}

interface ComponentReleaseState {
  releasedComponents: Map<string, ComponentRelease[]>;
  lastUpdateYear: number;
  currentAvailableComponents: Map<string, ComponentOption[]>;
}

/**
 * Service to manage dynamic component releases throughout the game
 * Components are released randomly each year with +1 increments to tech score and cost
 */
export class ComponentReleaseService {
  private static instance: ComponentReleaseService;
  private state: ComponentReleaseState;
  private readonly RELEASE_PROBABILITY = 0.65; // 65% chance of release each year

  // Company ranges
  private readonly COMPANY_RANGES = {
    'Ayaya Inc.': { min: 10, max: 30, prefix: 'A' },
    'Bee Corp': { min: 30, max: 50, prefix: 'B' },
    'Cee Incorporated': { min: 50, max: 70, prefix: 'C' },
    'DeeDee Inc.': { min: 70, max: 90, prefix: 'D' }
  };

  private constructor() {
    this.state = {
      releasedComponents: new Map(),
      lastUpdateYear: 2000,
      currentAvailableComponents: new Map()
    };
    this.initializeBaseComponents();
  }

  public static getInstance(): ComponentReleaseService {
    if (!ComponentReleaseService.instance) {
      ComponentReleaseService.instance = new ComponentReleaseService();
    }
    return ComponentReleaseService.instance;
  }

  /**
   * Initialize with base components available at game start
   */
  private initializeBaseComponents(): void {
    // Initialize the released components map for each category
    const categories = ['os', 'cpu', 'gpu', 'camera', 'battery', 'ram', 'storage', 'network'];

    categories.forEach(categoryId => {
      const releases: ComponentRelease[] = [];

      // Add initial components for each manufacturer
      Object.entries(this.COMPANY_RANGES).forEach(([manufacturer, range]) => {
        releases.push({
          componentId: `${range.prefix.toLowerCase()}${range.min}-${categoryId}`,
          manufacturer,
          releaseYear: 2000,
          releaseMonth: 1,
          techScore: range.min,
          cost: range.min,
          price: range.min * 2
        });
      });

      this.state.releasedComponents.set(categoryId, releases);
    });

    this.updateAvailableComponents(2000);
  }

  /**
   * Process yearly component releases with random chance
   */
  public processYearlyReleases(currentYear: number): ComponentRelease[] {
    if (currentYear <= this.state.lastUpdateYear) {
      return [];
    }

    const newReleases: ComponentRelease[] = [];
    const categories = ['os', 'cpu', 'gpu', 'camera', 'battery', 'ram', 'storage', 'network'];

    categories.forEach(categoryId => {
      const categoryReleases = this.state.releasedComponents.get(categoryId) || [];

      Object.entries(this.COMPANY_RANGES).forEach(([manufacturer, range]) => {
        // Check if company should release a new component
        if (Math.random() > this.RELEASE_PROBABILITY) {
          return; // No release this year for this company
        }

        // Find the latest component from this manufacturer
        const latestComponent = categoryReleases
          .filter(r => r.manufacturer === manufacturer)
          .sort((a, b) => b.techScore - a.techScore)[0];

        if (!latestComponent) return;

        // Check if we've reached the maximum for this company
        const nextScore = latestComponent.techScore + 1;
        if (nextScore > range.max) {
          return; // Company has reached its maximum tier
        }

        // Create new component release
        const newRelease: ComponentRelease = {
          componentId: `${range.prefix.toLowerCase()}${nextScore}-${categoryId}`,
          manufacturer,
          releaseYear: currentYear,
          releaseMonth: Math.floor(Math.random() * 12) + 1, // Random month
          techScore: nextScore,
          cost: nextScore,
          price: nextScore * 2
        };

        categoryReleases.push(newRelease);
        newReleases.push(newRelease);
      });

      this.state.releasedComponents.set(categoryId, categoryReleases);
    });

    this.state.lastUpdateYear = currentYear;
    this.updateAvailableComponents(currentYear);

    // Log releases for debugging
    if (newReleases.length > 0) {
      // console.log(`📦 Component releases for ${currentYear}:`, newReleases.map(r =>
      //   `${r.manufacturer}: ${r.componentId} (Score: ${r.techScore})`
      // ));
    }

    return newReleases;
  }

  /**
   * Update the available components based on current year
   */
  private updateAvailableComponents(currentYear: number): void {
    const categories = baseComponents;

    categories.forEach(category => {
      const categoryReleases = this.state.releasedComponents.get(category.id) || [];
      const availableOptions: ComponentOption[] = [];

      // Filter releases by year
      const availableReleases = categoryReleases.filter(r => r.releaseYear <= currentYear);

      // Convert releases to ComponentOptions
      availableReleases.forEach(release => {
        const companyInfo = Object.values(this.COMPANY_RANGES).find(r =>
          release.manufacturer === Object.keys(this.COMPANY_RANGES).find(k =>
            this.COMPANY_RANGES[k as keyof typeof this.COMPANY_RANGES].prefix === release.componentId[0].toUpperCase()
          )
        );

        if (!companyInfo) return;

        // Determine tier based on score
        let tier: 'budget' | 'mid' | 'premium' | 'ultra' = 'budget';
        if (release.techScore >= 70) tier = 'ultra';
        else if (release.techScore >= 50) tier = 'premium';
        else if (release.techScore >= 30) tier = 'mid';

        // Generate component option
        const option: ComponentOption = {
          name: `${release.componentId[0].toUpperCase()}${release.techScore} ${category.name}`,
          value: release.componentId,
          specs: this.generateSpecs(category.id, release.techScore),
          features: this.generateFeatures(category.id, release.techScore),
          manufacturer: release.manufacturer,
          releaseDate: {
            year: release.releaseYear,
            month: release.releaseMonth,
            day: 1
          },
          cost: release.cost,
          price: release.price,
          techScore: release.techScore,
          weight: 1,
          tier
        };

        availableOptions.push(option);
      });

      this.state.currentAvailableComponents.set(category.id, availableOptions);
    });
  }

  /**
   * Get available components for a specific category and year
   */
  public getAvailableComponents(categoryId: string, year: number, month?: number): ComponentOption[] {
    // Process any pending releases
    this.processYearlyReleases(year);

    const availableOptions = this.state.currentAvailableComponents.get(categoryId) || [];

    // Filter by release date if month is provided
    if (month !== undefined) {
      return availableOptions.filter(opt => {
        const releaseYear = opt.releaseDate.year;
        const releaseMonth = opt.releaseDate.month;
        return releaseYear < year || (releaseYear === year && releaseMonth <= month);
      });
    }

    return availableOptions.filter(opt => opt.releaseDate.year <= year);
  }

  /**
   * Get all available components across all categories
   */
  public getAllAvailableComponents(year: number, month?: number): Map<string, ComponentOption[]> {
    // Process any pending releases
    this.processYearlyReleases(year);

    const result = new Map<string, ComponentOption[]>();
    const categories = ['os', 'cpu', 'gpu', 'camera', 'battery', 'ram', 'storage', 'network'];

    categories.forEach(categoryId => {
      result.set(categoryId, this.getAvailableComponents(categoryId, year, month));
    });

    return result;
  }

  /**
   * Generate specs based on category and tech score
   */
  private generateSpecs(categoryId: string, techScore: number): string {
    switch (categoryId) {
      case 'os':
        if (techScore <= 30) return '1.0GHz, Basic UI';
        if (techScore <= 50) return '1.5GHz, Standard UI';
        if (techScore <= 70) return '2.0GHz, Premium UI';
        return '2.5GHz, Flagship UI';

      case 'cpu':
        if (techScore <= 30) return '1.2GHz Dual-core';
        if (techScore <= 50) return '2.0GHz Quad-core';
        if (techScore <= 70) return '2.8GHz Hexa-core';
        return '3.5GHz Octa-core';

      case 'gpu':
        if (techScore <= 30) return '400MHz Graphics';
        if (techScore <= 50) return '700MHz Graphics';
        if (techScore <= 70) return '1.0GHz Graphics';
        return '1.5GHz Graphics, RT cores';

      case 'camera':
        if (techScore <= 30) return '8MP, f/2.4';
        if (techScore <= 50) return '12MP, f/2.0';
        if (techScore <= 70) return '48MP, f/1.8, OIS';
        return '108MP, f/1.6, OIS, 8K';

      case 'battery':
        if (techScore <= 30) return '2500mAh, 15W charging';
        if (techScore <= 50) return '3500mAh, 30W charging';
        if (techScore <= 70) return '4500mAh, 65W charging';
        return '5000mAh, 120W charging';

      case 'ram':
        if (techScore <= 30) return '2GB LPDDR4';
        if (techScore <= 50) return '4GB LPDDR4X';
        if (techScore <= 70) return '8GB LPDDR5';
        return '12GB LPDDR5X';

      case 'storage':
        if (techScore <= 30) return '32GB eMMC';
        if (techScore <= 50) return '128GB UFS 2.1';
        if (techScore <= 70) return '256GB UFS 3.1';
        return '512GB UFS 4.0';

      case 'network':
        if (techScore <= 30) return '3G/WiFi 4';
        if (techScore <= 50) return '4G LTE/WiFi 5';
        if (techScore <= 70) return '5G Sub-6/WiFi 6';
        return '5G mmWave/WiFi 6E';

      default:
        return `Level ${techScore} specs`;
    }
  }

  /**
   * Generate features based on category and tech score
   */
  private generateFeatures(categoryId: string, techScore: number): string {
    if (techScore <= 30) return `Basic ${categoryId} functionality`;
    if (techScore <= 50) return `Standard ${categoryId} performance`;
    if (techScore <= 70) return `Advanced ${categoryId} capabilities`;
    return `Cutting-edge ${categoryId} technology`;
  }

  /**
   * Reset the service (for new game)
   */
  public reset(): void {
    this.state = {
      releasedComponents: new Map(),
      lastUpdateYear: 2000,
      currentAvailableComponents: new Map()
    };
    this.initializeBaseComponents();
  }

  /**
   * Get state for saving
   */
  public getState(): ComponentReleaseState {
    return this.state;
  }

  /**
   * Load state from save
   */
  public loadState(state: ComponentReleaseState): void {
    this.state = state;
  }
}

// Export singleton instance
export const componentReleaseService = ComponentReleaseService.getInstance();